import { CpuIcon, LoaderIcon, PowerOffIcon, TriangleAlertIcon } from "lucide-react";
import { memo, useCallback } from "react";
import Tooltip from "src/components/Tooltip";
import { Switch } from "src/components/ui/switch";
import { isMainWindow, isTauriRuntime } from "src/lib/runtime/environment";
import { type DocumentFarmStatus as FarmStatus, useDocumentFarmStore } from "src/stores/documentFarmStore";

const StatusIcon = memo(function StatusIcon({ status }: { status: FarmStatus }) {
  if (status === "off") return <PowerOffIcon className="w-3.5 h-3.5 text-muted-foreground" />;
  if (status === "retrying") return <TriangleAlertIcon className="w-3.5 h-3.5 text-amber-600" />;
  if (status === "working") {
    return <LoaderIcon className="w-3.5 h-3.5 text-primary animate-spin" />;
  }
  return <CpuIcon className="w-3.5 h-3.5 text-muted-foreground" />;
});

/**
 * What this machine is contributing to the shared document backlog, and the switch to stop it.
 *
 * Desktop-only and employee-only, matching the sweep itself — there is nothing to report anywhere
 * else, and a control for something that cannot run is worse than no control.
 */
export const DocumentFarmStatus = memo(function DocumentFarmStatus() {
  const enabled = useDocumentFarmStore((state) => state.enabled);
  const status = useDocumentFarmStore((state) => state.status);
  const remaining = useDocumentFarmStore((state) => state.remaining);
  const completed = useDocumentFarmStore((state) => state.completedThisSession);
  const failed = useDocumentFarmStore((state) => state.failedThisSession);
  const currentDocumentName = useDocumentFarmStore((state) => state.currentDocumentName);
  const publicStatementRead = useDocumentFarmStore((state) => state.publicStatementRead);
  const publicStatementReads = useDocumentFarmStore((state) => state.publicStatementReadsThisSession);
  const setEnabled = useDocumentFarmStore((state) => state.setEnabled);

  const onToggle = useCallback((next: boolean) => setEnabled(next), [setEnabled]);

  if (!isTauriRuntime() || !isMainWindow()) return null;

  const summary = !enabled
    ? "Paused"
    : publicStatementRead
      ? `Reading fraction.com statements ${publicStatementRead.filesRead}/${publicStatementRead.filesTotal}`
      : status === "working"
        ? `Reading ${remaining + 1} document${remaining ? "s" : ""}`
        : status === "retrying"
          ? "Retrying after an error"
          : "Up to date";

  return (
    <div className="flex flex-row items-center gap-2 px-3 py-2 border-t border-border">
      <Tooltip
        place="left"
        text={
          enabled
            ? "This Mac reads unprocessed documents whenever it has nothing else to do — the deal you have open first, then other active deals — so they are already searchable when someone opens them. Read " +
              `${completed} document${completed === 1 ? "" : "s"} since launch.` +
              (failed ? ` Skipped ${failed} that would not read.` : "") +
              (currentDocumentName ? ` Currently reading ${currentDocumentName}.` : "") +
              " Also reads fraction.com statements, in memory only" +
              (publicStatementReads ? ` (${publicStatementReads} since launch).` : ".")
            : "Background document processing is off on this Mac, including for the deal you have open. Documents will be read when someone opens them instead."
        }
      >
        <StatusIcon status={enabled ? (publicStatementRead ? "working" : status) : "off"} />
      </Tooltip>
      <div className="flex flex-col leading-tight">
        <span className="text-xs text-foreground">Background processing</span>
        <span className="text-[11px] text-muted-foreground">{summary}</span>
      </div>
      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        aria-label="Background document processing"
        className="ml-auto"
      />
    </div>
  );
});
